import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import SuggestedEditIcon from '@atlaskit/icon-lab/core/suggested-edit';

import { Ati } from '@atlassian/ari';

import { SMART_CREATE_PROMPT } from '@confluence/named-routes';
import { getApolloClient } from '@confluence/graphql';

import type { UseMenuItemProps, UseMenuItemReturnProps } from '.';

import { useHybridLinkClick } from '../../CreateContentPopover/CreateContentPopoverItems/hybridLinkHelper';
import type {
	GetPromotedSpacesQueryVariables,
	GetPromotedSpacesQuery as GetPromotedSpacesQueryType,
} from '../../queries/__types__/GetPromotedSpacesQuery';
import { GetPromotedSpacesQuery } from '../../queries/GetPromotedSpacesQuery.experimentalgraphql';

const i18n = defineMessages({
	smartCreateTitle: {
		id: 'create-blank-fabric-page.create.content.smart-create.title',
		defaultMessage: 'Get started with AI',
		description: 'title indicating that this option will trigger an AI guided creation workflow',
	},
});

const getSpaceKeyForCreation = async (): Promise<string> => {
	const spaceKeyRequest: GetPromotedSpacesQueryVariables = {
		promotedSpaceKey: undefined,
	};
	const spaceKeyResponse: GetPromotedSpacesQueryType = (
		await getApolloClient().query({
			query: GetPromotedSpacesQuery,
			variables: spaceKeyRequest,
		})
	).data;
	return spaceKeyResponse?.experimentalPromotedSpaces?.nodes?.[0]?.id ?? '';
};

/**
 * This is a menu item for an internal demo/spike of a new AI powered creation workflow.
 */
export function useAISmartCreateItem({
	spaceKey,
	onClick,
}: UseMenuItemProps): UseMenuItemReturnProps {
	const { formatMessage } = useIntl();

	const [fetchedSpaceKey, setFetchedSpaceKey] = useState<string>('');

	// Promoted Spaces query should be cached, but we do have a race condition here for global create where there
	// can potentially be missing space key. This is fine for the early spike stage.
	useEffect(() => {
		void getSpaceKeyForCreation().then(setFetchedSpaceKey);
	}, []);

	const createHref = SMART_CREATE_PROMPT.toUrl(undefined, {
		query: { spaceKey: spaceKey ?? fetchedSpaceKey },
	});

	const hybridPageLinkClick = useHybridLinkClick(createHref);

	const handleClick = useCallback(
		(e: React.MouseEvent | React.KeyboardEvent) => {
			onClick('createAISmartCreatePopoverItem');
			hybridPageLinkClick(e);
		},
		[onClick, hybridPageLinkClick],
	);

	return {
		ati: Ati.parse('ati:cloud:confluence:page'), // todo: this is incorrect, we don't know the type identifier yet
		children: formatMessage(i18n.smartCreateTitle),
		onClick: handleClick,
		elemBefore: SuggestedEditIcon,
		href: createHref,
		testId: `create-menu-link-item`,
	};
}
